// The links a customer was sent (gap #18: portalLinks.list): what each opens, where it went, when
// it was opened, and whether it still works, the newest first, a page at a time (list caps, BL-110).
// One can be turned off with a reason (portalLinks.revoke, customers.edit), say a changed number or a
// forwarded email; the server's words when it refuses.

import { useState } from 'react';
import { dateText, dateTimeText } from '@flowology/kernel';
import { RpcError } from '@flowology/client';
import { client } from '../api.ts';
import { usePaged } from '../blueprint/paged.ts';
import { shownText } from '../shared/pages.ts';
import { SaveButton } from '../shared/SaveButton.tsx';
import { scrollFocus } from '../shared/scrollFocus.ts';

export interface PortalLink {
  id: string;
  scope: string;
  entityId: string;
  sentTo: string | null;
  state: 'ok' | 'expired' | 'revoked' | 'unknown';
  openCount: number;
  lastOpenedAt: string | null;
  createdAt: string;
}

/** portalLinks.list's answer, a page at a time: `total` counts every link the customer was sent. */
export interface PortalLinksPage {
  links: PortalLink[];
  total: number;
  next: string | null;
}

/** How many links are read at a time; the rest come on "Load more" (list caps, BL-110). */
export const LINKS_PAGE = 100;

/** portalLinks.list's input for a customer, a page at a time. */
export const linksInput = (customerId: string): Record<string, unknown> => ({
  customerId,
  limit: LINKS_PAGE,
});

const linksOf = (p: PortalLinksPage) => p.links;

const OPENS: Record<string, string> = { estimate: 'An estimate', invoice: 'An invoice' };
const STATE: Record<PortalLink['state'], [string, string]> = {
  ok: ['ok', 'Works'],
  expired: ['', 'Expired'],
  revoked: ['bad', 'Turned off'],
  unknown: ['', 'Unknown'],
};

/** "Opened 3 times, last Oct 15, 5:18 p.m." or "Not opened yet". */
export function openedText(l: Pick<PortalLink, 'openCount' | 'lastOpenedAt'>, timeZone: string): string {
  if (!l.openCount) return 'Not opened yet';
  const times = l.openCount === 1 ? 'Opened once' : `Opened ${l.openCount} times`;
  return l.lastOpenedAt ? `${times}, last ${dateTimeText(l.lastOpenedAt, timeZone, { year: false })}` : times;
}

export function PortalLinks({
  customerId,
  canRevoke,
  timeZone,
  inZone = false,
}: {
  customerId: string;
  /** customers.edit */
  canRevoke: boolean;
  timeZone: string;
  /** Blueprint: a part of the record's work, not a section of its own */
  inZone?: boolean;
}) {
  const Part = inZone ? 'div' : 'section';
  const list = usePaged('portalLinks.list', linksInput(customerId), linksOf);
  const [off, setOff] = useState<{ id: string; reason: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const links = list.rows ?? (list.error ? [] : null);
  if (!links) return null;
  const turnOff = async () => {
    if (!off) return;
    setBusy(true);
    setError(null);
    try {
      await client.call('portalLinks.revoke', { linkId: off.id, reason: off.reason.trim() });
      setOff(null);
      list.reload();
    } catch (e) {
      setError(e instanceof RpcError ? e.message : 'That did not work. Try again.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <Part className="portal-links" aria-label="Links sent">
      <h3>Links sent</h3>
      {links.length === 0 ? (
        <p className="muted">No estimate or invoice links have been sent to this customer.</p>
      ) : (
        <div className="tablewrap" ref={scrollFocus}>
          <table className="t links-sent" aria-label="Links sent">
            <thead>
              <tr>
                <th>Opens</th>
                <th>Sent to</th>
                {/* on a phone, said under what it opens instead, so the table fits */}
                <th className="wide-col">Sent</th>
                <th className="wide-col">Opened</th>
                <th>Works?</th>
                {canRevoke && <th aria-label="Turn off" />}
              </tr>
            </thead>
            <tbody>
              {links.map((l) => {
                const [tone, word] = STATE[l.state];
                return (
                  <tr key={l.id}>
                    {/* on a phone its Sent and Opened, said under it (CSS, from data-phone) */}
                    <td
                      data-phone={`Sent ${dateText(l.createdAt, timeZone, { year: false })} · ${openedText(l, timeZone)}`}
                    >
                      {OPENS[l.scope] ?? 'A page'}
                    </td>
                    <td>{l.sentTo ?? '—'}</td>
                    <td className="nowrap wide-col">{dateText(l.createdAt, timeZone, { year: false })}</td>
                    <td className="wide-col">{openedText(l, timeZone)}</td>
                    <td>
                      <span className={`chip say ${tone}`}>{word}</span>
                    </td>
                    {canRevoke && (
                      <td>
                        {l.state === 'ok' && off?.id !== l.id && (
                          <button className="btn soft" onClick={() => setOff({ id: l.id, reason: '' })}>
                            Turn off…
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {(list.more || list.busy) && (
        <p className="links-more">
          <span className="muted small">{shownText(links.length, list.first?.total ?? links.length)}</span>
          <button type="button" className="btn soft" disabled={list.busy} onClick={() => list.more?.()}>
            {list.busy ? 'Loading…' : 'Load more'}
          </button>
        </p>
      )}
      {off && (
        <div className="fgrid inline-form">
          <label className="wide">
            <span className="flabel">
              Why turn it off? (the customer’s number changed, it was forwarded…)
            </span>
            <input
              className="field"
              value={off.reason}
              autoFocus
              onChange={(e) => setOff({ ...off, reason: e.target.value })}
            />
          </label>
          <div className="row">
            <SaveButton
              className="btn danger"
              busy={busy}
              busyLabel="Turning off…"
              error={error}
              disabled={!off.reason.trim()}
              onClick={() => void turnOff()}
            >
              Turn the link off
            </SaveButton>
            <button className="btn quiet" onClick={() => setOff(null)}>
              Keep it working
            </button>
          </div>
        </div>
      )}
    </Part>
  );
}
